import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import ToastService from '~/services/toast.service';

import { i18n } from '~/utils';

import { queryKeysInvoiceReprocessing } from '../queryKeys';

import { approveStoppers } from './approveStoppers';

/**
 * A custom hook to approve the stoppers for an invoice process with a given process ID.
 * Uses the `useMutation` hook from `@tanstack/react-query` internally.
 * @param processId - The ID of the invoice process.
 * @returns A promise that resolves when the stoppers are approved.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/approve_stoppers_invoice_process__process_id__approve_stoppers_put
 */
export const useMutationApproveStoppers = (options?: UseMutationOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (processId: UUID) => approveStoppers(processId),
    onSettled(_data, _error, processId) {
      queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceReprocessing.get(processId),
      });
    },
    onSuccess() {
      ToastService.success(
        i18n.t(($) => $.approveStoppers.success, {
          ns: 'data',
        }),
      );
    },
    ...options,
  });
};
